/**
 * Reverse Fact Find (RFF) overlay: how review edits are stored over the Fact
 * Find baseline, and the one equality rule every RFF comparison uses.
 *
 * Array sections are stored as PATCHES (Oct 2026) so a review edit never
 * freezes the list: later Fact Find edits and new Fact Find rows still show on
 * the review and survive Set New Baseline.
 *   overlay[section][key]              — { id, ...changedFields } patches and full new rows
 *   overlay[section][key + 'RemovedIds'] — baseline ids removed on the review (marks patch mode)
 *   overlay[section][key + 'AddedIds']   — ids of rows added on the review
 * Overlays saved before Oct 2026 hold a full copy of the list ("legacy"):
 * the overlay array decides which rows exist. They keep that meaning until
 * their next edit converts them — rows the frozen copy hides are KEPT on
 * conversion (owner, Oct 2026). Lists whose baseline rows lack unique ids stay
 * in legacy form.
 *
 * ONE file, CommonJS body, `.js` extension: the browser imports it and the
 * server twins (reverseFactFindMerge.cjs, circumstanceChanges.cjs) require it.
 * No object spread (see superGuarantee.js).
 */

var RFF_ARRAY_KEYS = {
  personal: ['dependants'],
  employment: ['jobs'],
  financial: ['incomes'],
  assets: ['items'],
  superannuation: ['currentFunds'],
  insurance: ['covers'],
  contributions: ['items'],
  beneficiaries: ['items'],
};

var REMOVED_SUFFIX = 'RemovedIds';
var ADDED_SUFFIX = 'AddedIds';

function isPlainObject(value) {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

function isArrayKey(section, key) {
  var keys = RFF_ARRAY_KEYS[section];
  return Boolean(keys && keys.indexOf(key) >= 0);
}

/** A row's id as a string, or null when it has none ('' counts as none). */
function idKey(row) {
  return row && row.id != null && row.id !== '' ? String(row.id) : null;
}

/** Unset, blank, off and empty all mean "no answer" (an explicit 0 does not). */
function isBlank(value) {
  if (value == null || value === '' || value === false) return true;
  if (Array.isArray(value)) return value.length === 0;
  if (isPlainObject(value)) {
    return Object.keys(value).every(function (k) { return isBlank(value[k]); });
  }
  return false;
}

function isNumericString(value) {
  return typeof value === 'string' && /^\s*-?[\d,]*\.?\d+\s*$/.test(value);
}

function toNumber(value) {
  return typeof value === 'number' ? value : Number(String(value).replace(/,/g, ''));
}

/**
 * The RFF's one equality rule (page highlights, overlay writes, merge and
 * Changed Circumstances). Blank values equal each other; a number equals a
 * numeric string ('5000.00' == 5000) but two strings compare exactly (postcode
 * '0800' != '800'); arrays compare in order; objects over the union of keys.
 */
function rffValuesEqual(a, b) {
  if (a === b) return true;
  var aBlank = isBlank(a);
  var bBlank = isBlank(b);
  if (aBlank || bBlank) return aBlank && bBlank;
  if (typeof a === 'number' || typeof b === 'number') {
    var aNum = typeof a === 'number' || isNumericString(a);
    var bNum = typeof b === 'number' || isNumericString(b);
    return aNum && bNum && toNumber(a) === toNumber(b);
  }
  if (Array.isArray(a) || Array.isArray(b)) {
    if (!Array.isArray(a) || !Array.isArray(b) || a.length !== b.length) return false;
    for (var i = 0; i < a.length; i += 1) {
      if (!rffValuesEqual(a[i], b[i])) return false;
    }
    return true;
  }
  if (isPlainObject(a) && isPlainObject(b)) {
    var keys = {};
    Object.keys(a).forEach(function (k) { keys[k] = true; });
    Object.keys(b).forEach(function (k) { keys[k] = true; });
    return Object.keys(keys).every(function (k) { return rffValuesEqual(a[k], b[k]); });
  }
  return String(a) === String(b);
}

/**
 * Baseline index of each merged row: by id, or by position when neither row
 * has an id (rows saved before ids existed). -1 for a row added on the review.
 */
function matchBaselineRows(baseRows, mergedRows) {
  var base = Array.isArray(baseRows) ? baseRows : [];
  return (Array.isArray(mergedRows) ? mergedRows : []).map(function (row, index) {
    var key = idKey(row);
    if (key) return base.findIndex(function (b) { return idKey(b) === key; });
    return base[index] && !idKey(base[index]) ? index : -1;
  });
}

/**
 * Pair baseline rows with current rows: by id (each baseline row once), or by
 * position when neither has an id.
 * @returns {{ pairs: Array<{baseIndex, currIndex, base, curr}>, added: Array<{currIndex, curr}>, removed: Array<{baseIndex, base}> }}
 */
function pairRows(baseRows, currRows) {
  var base = Array.isArray(baseRows) ? baseRows : [];
  var curr = Array.isArray(currRows) ? currRows : [];
  var claimed = {};
  var pairs = [];
  var added = [];
  curr.forEach(function (row, currIndex) {
    var key = idKey(row);
    var baseIndex = -1;
    if (key) {
      baseIndex = base.findIndex(function (b, i) { return !claimed[i] && idKey(b) === key; });
    } else if (base[currIndex] && !idKey(base[currIndex]) && !claimed[currIndex]) {
      baseIndex = currIndex;
    }
    if (baseIndex >= 0) {
      claimed[baseIndex] = true;
      pairs.push({ baseIndex: baseIndex, currIndex: currIndex, base: base[baseIndex], curr: row });
    } else {
      added.push({ currIndex: currIndex, curr: row });
    }
  });
  var removed = [];
  base.forEach(function (row, baseIndex) {
    if (!claimed[baseIndex]) removed.push({ baseIndex: baseIndex, base: row });
  });
  return { pairs: pairs, added: added, removed: removed };
}

/** Every row is an object with a unique, non-blank id (so the list can be patched). */
function isPatchEligible(rows) {
  if (!Array.isArray(rows)) return false;
  var seen = {};
  return rows.every(function (row) {
    var key = isPlainObject(row) ? idKey(row) : null;
    if (!key || seen[key]) return false;
    seen[key] = true;
    return true;
  });
}

function isPatchMode(sectionOverlay, key) {
  return Boolean(sectionOverlay) && Array.isArray(sectionOverlay[key + REMOVED_SUFFIX]);
}

function keySet(list) {
  var set = {};
  (Array.isArray(list) ? list : []).forEach(function (id) {
    if (id != null && id !== '') set[String(id)] = true;
  });
  return set;
}

/**
 * One array of the merged (effective) view.
 * @param {Array|undefined} baseRows — the Fact Find rows
 * @param {object|undefined} sectionOverlay — overlay[section]
 * @param {string} key
 * @param {*} [emptyValue] — returned when neither side has the array; [] when
 *   omitted (pass undefined explicitly to leave the key unset)
 */
function mergeOverlayArray(baseRows, sectionOverlay, key, emptyValue) {
  var edited = sectionOverlay ? sectionOverlay[key] : undefined;
  var hasBase = Array.isArray(baseRows);
  var base = hasBase ? baseRows : [];
  if (!Array.isArray(edited)) {
    if (hasBase) return baseRows;
    return arguments.length > 3 ? emptyValue : [];
  }

  if (!isPatchMode(sectionOverlay, key)) {
    // Legacy: the overlay array decides which rows exist. An overlay row with
    // an id only merges with the id-matched baseline row — a new RFF row must
    // not inherit an unrelated row's fields.
    return edited.map(function (row, index) {
      if (row == null) return base[index] != null ? base[index] : row;
      var baseRow;
      if (isPlainObject(row)) {
        var key2 = idKey(row);
        baseRow = key2
          ? base.find(function (b) { return isPlainObject(b) && idKey(b) === key2; })
          : base[index];
      }
      return isPlainObject(baseRow) && isPlainObject(row) ? Object.assign({}, baseRow, row) : row;
    });
  }

  var removed = keySet(sectionOverlay[key + REMOVED_SUFFIX]);
  var addedList = sectionOverlay[key + ADDED_SUFFIX];
  var added = Array.isArray(addedList) ? keySet(addedList) : null;
  var patchById = {};
  edited.forEach(function (row) {
    var k = isPlainObject(row) ? idKey(row) : null;
    if (k) patchById[k] = row;
  });

  var result = [];
  var seen = {};
  base.forEach(function (row) {
    var k = isPlainObject(row) ? idKey(row) : null;
    if (k && removed[k]) return;
    if (k) seen[k] = true;
    // A row already carrying an added row's id is a merged view being merged
    // again: keep it as it is so the merge stays idempotent.
    if (k && patchById[k] && !(added && added[k])) {
      result.push(Object.assign({}, row, patchById[k]));
    } else {
      result.push(row);
    }
  });
  edited.forEach(function (row) {
    var k = isPlainObject(row) ? idKey(row) : null;
    if (!k || seen[k]) return;
    // An id that is neither in the baseline nor marked added is a patch whose
    // Fact Find row was deleted since: drop it rather than show a stub row.
    if (added ? added[k] : true) {
      seen[k] = true;
      result.push(row);
    }
  });
  return result;
}

/** A merged section without the overlay's bookkeeping keys. */
function stripOverlayMarkers(sectionObj, section) {
  if (!isPlainObject(sectionObj)) return sectionObj;
  var out = Object.assign({}, sectionObj);
  (RFF_ARRAY_KEYS[section] || []).forEach(function (key) {
    delete out[key + REMOVED_SUFFIX];
    delete out[key + ADDED_SUFFIX];
  });
  return out;
}

function freshId(used, seed) {
  var id = typeof seed === 'number' ? seed : Date.now();
  while (used[String(id)]) id += 1;
  used[String(id)] = true;
  return id;
}

/**
 * The overlay after one RFF write (pure). `value` is what the section shows:
 * a scalar, or the whole merged array for a list. Returns the same overlay
 * object when nothing changed.
 * @param {object} baseline — the Fact Find (client record)
 * @param {object} overlay — current reverseFactFind
 * @param {{ now?: number }} [options]
 */
function applyReverseFactFindWrite(baseline, overlay, section, key, value, options) {
  var prevOverlay = isPlainObject(overlay) ? overlay : {};
  var prevSection = isPlainObject(prevOverlay[section]) ? prevOverlay[section] : {};
  var nextSection = Object.assign({}, prevSection);
  var baseSection = baseline && isPlainObject(baseline[section]) ? baseline[section] : {};
  var opts = options || {};

  if (isArrayKey(section, key)) {
    var baseRows = Array.isArray(baseSection[key]) ? baseSection[key] : [];
    var incoming = Array.isArray(value) ? value : [];
    if (!isPatchEligible(baseRows)) {
      delete nextSection[key + REMOVED_SUFFIX];
      delete nextSection[key + ADDED_SUFFIX];
      if (rffValuesEqual(incoming, baseRows)) delete nextSection[key];
      else nextSection[key] = incoming;
    } else {
      // Converting a frozen (legacy) copy: only rows it showed can count as
      // removed; Fact Find rows it hid are kept (owner, Oct 2026). A copied
      // row without an id showed the Fact Find row at its position (as the
      // legacy merge matches it).
      var wasLegacy = Array.isArray(prevSection[key]) && !isPatchMode(prevSection, key);
      var shownBefore = wasLegacy
        ? keySet(prevSection[key].map(function (row, index) {
          return idKey(row) || (isPlainObject(row) && baseRows[index] ? idKey(baseRows[index]) : null);
        }))
        : null;
      var baseById = {};
      var used = {};
      baseRows.forEach(function (row) {
        baseById[idKey(row)] = row;
        used[idKey(row)] = true;
      });
      incoming.forEach(function (row) {
        var k = isPlainObject(row) ? idKey(row) : null;
        if (k) used[k] = true;
      });

      var patches = [];
      var addedIds = [];
      var seen = {};
      var nextIdSeed = typeof opts.now === 'number' ? opts.now : Date.now();
      incoming.forEach(function (row) {
        if (!isPlainObject(row)) return;
        var k = idKey(row);
        if (k && baseById[k] && !seen[k]) {
          seen[k] = true;
          var baseRow = baseById[k];
          var patch = { id: row.id };
          var changed = false;
          Object.keys(row).forEach(function (field) {
            if (field === 'id') return;
            if (!rffValuesEqual(row[field], baseRow[field])) {
              patch[field] = row[field] === undefined ? null : row[field];
              changed = true;
            }
          });
          // A field the review deleted (e.g. a cleared catalog link) is stored
          // blank, or the merge would bring the Fact Find's value back.
          Object.keys(baseRow).forEach(function (field) {
            if (field in row || isBlank(baseRow[field])) return;
            patch[field] = null;
            changed = true;
          });
          if (changed) patches.push(patch);
          return;
        }
        var newRow = row;
        if (!k || seen[k] || baseById[k]) {
          newRow = Object.assign({}, row, { id: freshId(used, nextIdSeed) });
        }
        seen[idKey(newRow)] = true;
        patches.push(newRow);
        addedIds.push(newRow.id);
      });

      var removedIds = [];
      baseRows.forEach(function (row) {
        var k = idKey(row);
        if (seen[k]) return;
        if (shownBefore && !shownBefore[k]) return;
        removedIds.push(row.id);
      });

      if (!patches.length && !removedIds.length) {
        delete nextSection[key];
        delete nextSection[key + REMOVED_SUFFIX];
        delete nextSection[key + ADDED_SUFFIX];
      } else {
        nextSection[key] = patches;
        nextSection[key + REMOVED_SUFFIX] = removedIds;
        nextSection[key + ADDED_SUFFIX] = addedIds;
      }
    }
  } else if (rffValuesEqual(value, baseSection[key])) {
    delete nextSection[key];
  } else {
    nextSection[key] = value === undefined ? null : value;
  }

  if (JSON.stringify(nextSection) === JSON.stringify(prevSection)) return prevOverlay;
  var nextOverlay = Object.assign({}, prevOverlay);
  if (Object.keys(nextSection).length) nextOverlay[section] = nextSection;
  else delete nextOverlay[section];
  return nextOverlay;
}

function readPath(value, parts) {
  var current = value;
  for (var i = 0; i < parts.length; i += 1) {
    if (current == null) return undefined;
    current = current[parts[i]];
  }
  return current;
}

/**
 * Whether an RFF field differs from the "before" data (the Fact Find, or the
 * committed snapshot after Set New Baseline). List rows are matched by id, so
 * removing a row doesn't light up every row after it.
 * @param {object} before — sections the review is compared with
 * @param {object} merged — the effective view the page shows
 * @param {string} section
 * @param {string|string[]} path — 'field' or '<arrayKey>.<index>.<field...>', or the
 *   parts as an array (for keys that contain dots, e.g. premium bundle keys)
 */
function isReviewFieldChanged(before, merged, section, path) {
  var parts = Array.isArray(path) ? path.map(String) : String(path || '').split('.');
  var beforeSection = before && before[section];
  var mergedSection = merged && merged[section];
  if (parts.length >= 3 && isArrayKey(section, parts[0]) && /^\d+$/.test(parts[1])) {
    var index = Number(parts[1]);
    var mergedRows = (mergedSection && mergedSection[parts[0]]) || [];
    var baseRows = (beforeSection && beforeSection[parts[0]]) || [];
    var row = mergedRows[index];
    var matchIndex = matchBaselineRows(baseRows, [row])[0];
    if (!idKey(row) && baseRows[index] && !idKey(baseRows[index])) matchIndex = index;
    var baseRow = matchIndex >= 0 ? baseRows[matchIndex] : undefined;
    var rest = parts.slice(2);
    return !rffValuesEqual(readPath(baseRow, rest), readPath(row, rest));
  }
  return !rffValuesEqual(readPath(beforeSection, parts), readPath(mergedSection, parts));
}

/**
 * The sections a review is compared with after Set New Baseline: the
 * committed snapshot of the first commit, kept until Finalise File clears it
 * (owner, Oct 2026). null when nothing has been committed.
 */
function committedReviewBaseline(clientData) {
  var committed = clientData && clientData.reverseFactFindCommitted;
  return committed && isPlainObject(committed.baseline) ? committed.baseline : null;
}

module.exports = {
  RFF_ARRAY_KEYS: RFF_ARRAY_KEYS,
  REMOVED_SUFFIX: REMOVED_SUFFIX,
  ADDED_SUFFIX: ADDED_SUFFIX,
  idKey: idKey,
  isBlank: isBlank,
  rffValuesEqual: rffValuesEqual,
  matchBaselineRows: matchBaselineRows,
  pairRows: pairRows,
  isPatchEligible: isPatchEligible,
  isPatchMode: isPatchMode,
  mergeOverlayArray: mergeOverlayArray,
  stripOverlayMarkers: stripOverlayMarkers,
  applyReverseFactFindWrite: applyReverseFactFindWrite,
  isReviewFieldChanged: isReviewFieldChanged,
  committedReviewBaseline: committedReviewBaseline,
};
